export type AppointmentMode = 'appt' | 'fcfs'

export type AppointmentLoadingType = 'live' | 'drop'

export type AppointmentType = 'requested' | 'pre-set'

export type AppointmentSlot = {
  start: string
  end: string
  type: 'after' | 'before' | 'between' | 'at'
}

export type OpenDockAppointmentStatus =
  | 'requested'
  | 'scheduled'
  | 'arrived'
  | 'inprogress'
  | 'completed'
  | 'cancelled'
  | 'noshow'

export type ShipperGuideAppointmentStatus = 'pending' | 'requested' | 'confirmed'

export type AppointmentStatus = OpenDockAppointmentStatus | ShipperGuideAppointmentStatus

export const APPOINTMENT_STATUS = {
  // ShipperGuide statuses
  PENDING: 'pending',
  REQUESTED: 'requested',
  CONFIRMED: 'confirmed',
  // OpenDock statuses
  SCHEDULED: 'scheduled',
  ARRIVED: 'arrived',
  INPROGRESS: 'inprogress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  NOSHOW: 'noshow',
} as const satisfies Record<string, AppointmentStatus>

/** Statuses where appointment is currently active — carrier can view it. */
export const ACTIVE_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = [
  APPOINTMENT_STATUS.CONFIRMED,
  APPOINTMENT_STATUS.SCHEDULED,
  APPOINTMENT_STATUS.ARRIVED,
  APPOINTMENT_STATUS.INPROGRESS,
  APPOINTMENT_STATUS.COMPLETED,
]

/** All statuses that represent an appointment that was ever scheduled (SG or OD). */
export const CONFIRMED_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = [
  ...ACTIVE_APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS.CANCELLED,
  APPOINTMENT_STATUS.NOSHOW,
]

/** Statuses where carrier can reschedule the appointment. */
export const RESCHEDULABLE_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = [
  APPOINTMENT_STATUS.CONFIRMED,
  APPOINTMENT_STATUS.REQUESTED,
  APPOINTMENT_STATUS.SCHEDULED,
]

/** Statuses where carrier can reset the appointment. */
export const RESETABLE_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = [
  APPOINTMENT_STATUS.CANCELLED,
  APPOINTMENT_STATUS.NOSHOW,
]

/** Statuses where appointment is read-only — no carrier actions allowed. */
export const READONLY_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = [
  APPOINTMENT_STATUS.INPROGRESS,
  APPOINTMENT_STATUS.COMPLETED,
]

export type StopAppointment = {
  channel: string
  misschedulingNote?: string
  misschedulingReason?: string
  mode: AppointmentMode
  loadingType: AppointmentLoadingType
  scheduledAt?: string
  suggestedWindowStart?: string
  suggestedWindowEnd?: string
  scheduledWindowStart?: string
  scheduledWindowEnd?: string
  type: AppointmentType
  status: AppointmentStatus
  scheduledBy?: string
  confirmationNumber?: string
  scheduledDate?: AppointmentSlot
  scheduledTime?: AppointmentSlot
  suggestedDate?: AppointmentSlot
  suggestedTime?: AppointmentSlot
  externalReferenceUuid?: string
  outOfTime?: boolean
  previousScheduledWindowStart?: string
  previousScheduledWindowEnd?: string
  acknowledgeOutOfTime?: boolean
  pendingSchedule?: boolean
}

export const enum AppointmentTimeOptions {
  At = 'at',
  Between = 'between',
}

export const enum AppointmentModeOptions {
  Appt = 'appt',
  Fcfs = 'fcfs',
}

export const enum AppointmentLoadingTypeOptions {
  Live = 'live',
  Drop = 'drop',
}

export const enum AppointmentMisschedulingReasonOptions {
  FirstAvailable = 'first-available',
  InsufficientLeadTime = 'insufficient-lead-time',
  WarehouseError = 'warehouse-error',
  HolidayOrClosed = 'holiday-or-closed',
  PowerOutage = 'power-outage',
  DelayedAtShipper = 'delayed-at-shipper',
  Reconsigned = 'reconsigned',
  DeliveryRefused = 'delivery-refused',
  DriverDelayedOnPreviousShipment = 'driver-delayed-on-previous-shipment',
  CapacityConstraints = 'capacity-constraints',
  MechanicalBreakdown = 'mechanical-breakdown',
  DropTrailerConstraints = 'drop-trailer-constraints',
  MissedAppointment = 'missed-appointment',
  Traffic = 'traffic',
  DriverAccident = 'driver-accident',
  DOTInspection = 'dot-inspection',
  RailDelay = 'rail-delay',
  DriverError = 'driver-error',
  HoursOfServiceCompliance = 'hours-of-service-compliance',
  DispatcherError = 'dispatcher-error',
  Weather = 'weather',
  RoadClosure = 'road-closure',
  Other = 'other',
}
